/**
 * App Shell
 * 
 * The new navigation layout following the main-nav-spec.md
 * Uses Activity Rail + Pop-Out Panels instead of expandable sidebar.
 * 
 * Per spec:
 * - Navigation never resizes or shifts the main workspace
 * - Pop-outs are transient, not persistent UI panels
 * - Navigation must feel tool-like, not page-like
 * - The workspace is always the primary focus
 */
import React, { useState, useCallback, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { ActivityRail, type RailItem } from './ActivityRail';
import { PopOutPanel } from './PopOutPanel';
import { ProjectsPopOut, EditorPopOut, RunsPopOut, ResultsPopOut, ProfilePopOut } from './popouts';
import { DocsDrawer } from '../docs';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import MobileTopBar from './MobileTopBar';
import { AppTopBar } from './AppTopBar';
import { TopBarProvider, useTopBar } from '../../contexts/TopBarContext';
import { useDocsStore } from '../../store/docsStore';
import { useJobsIntegration } from '../../hooks/jobs/useJobsIntegration';

// Minimum editor width to ensure usability
const MIN_EDITOR_WIDTH = 600;

// Panel titles for each rail item
const PANEL_TITLES: Record<RailItem, string> = {
    projects: 'Projects',
    editor: 'Editor',
    runs: 'Runs',
    results: 'Results',
    admin: 'Admin',
    docs: 'Documentation',
    notifications: 'Notifications',
    profile: 'Profile',
};

export const AppShell: React.FC = () => {
    return (
        <TopBarProvider>
            <AppShellContent />
        </TopBarProvider>
    );
};

const AppShellContent: React.FC = () => {
    const { isMobileOrTablet } = useBreakpoint();
    const { config } = useTopBar();
    const [activePanel, setActivePanel] = useState<RailItem | null>(null);
    const [focusMode, setFocusMode] = useState(false);

    // Initialize jobs integration service to sync optimization events to Jobs store
    useJobsIntegration();

    // Docs panel state for workspace-aware layout
    const docsOpen = useDocsStore((s) => s.isOpen);
    const docsPinned = useDocsStore((s) => s.isPinned);
    const docsWidth = useDocsStore((s) => s.width);
    const setDocsWidth = useDocsStore((s) => s.setWidth);

    // Calculate if docs should act as a workspace split (pinned + open + not in focus mode)
    const docsAsSplit = docsOpen && docsPinned && !focusMode;

    // Handle rail item click
    const handleRailClick = useCallback((item: RailItem) => {
        // Exit focus mode when opening a panel
        if (focusMode) {
            setFocusMode(false);
        }

        // Toggle: if clicking the same item, close the panel
        if (activePanel === item) {
            setActivePanel(null);
        } else {
            setActivePanel(item);
        }
    }, [activePanel, focusMode]);

    // Close panel
    const handleClosePanel = useCallback(() => {
        setActivePanel(null);
    }, []);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Ctrl/Cmd + Alt + F: Toggle focus mode (collapse all panels)
            if ((e.metaKey || e.ctrlKey) && e.altKey && e.key === 'f') {
                e.preventDefault();
                setFocusMode(prev => !prev);
                if (!focusMode) {
                    // Entering focus mode: close pop-out panel
                    setActivePanel(null);
                }
                return;
            }

            // Check for Cmd/Ctrl + number
            if (e.metaKey || e.ctrlKey) {
                const keyMap: Record<string, RailItem> = {
                    '1': 'projects',
                    '2': 'editor',
                    '3': 'runs',
                    '4': 'results',
                };

                if (keyMap[e.key]) {
                    e.preventDefault();
                    handleRailClick(keyMap[e.key]);
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleRailClick, focusMode]);

    // Render panel content based on active item
    const renderPanelContent = () => {
        switch (activePanel) {
            case 'projects':
                return <ProjectsPopOut onClose={handleClosePanel} />;
            case 'editor':
                return <EditorPopOut onClose={handleClosePanel} />;
            case 'runs':
                return <RunsPopOut onClose={handleClosePanel} />;
            case 'results':
                return <ResultsPopOut onClose={handleClosePanel} />;
            case 'notifications':
                return (
                    <div className="p-4 text-center text-gray-500 text-sm">
                        No notifications
                    </div>
                );
            case 'profile':
                return <ProfilePopOut onClose={handleClosePanel} />;
            default:
                return null;
        }
    };

    // Mobile layout
    if (isMobileOrTablet) {
        return (
            <div className="app-shell min-h-screen bg-[#0a0e14]">
                <MobileTopBar />
                <main className="pt-16">
                    <Outlet />
                </main>
            </div>
        );
    }

    // Calculate effective docs width, ensuring minimum editor width
    // Activity rail is ~56px, so available = viewport - 56 - docsWidth
    const activityRailWidth = 56;
    const availableForEditor = typeof window !== 'undefined'
        ? window.innerWidth - activityRailWidth - docsWidth
        : 1000;

    // If editor would be too narrow, clamp docs width
    useEffect(() => {
        if (docsAsSplit && availableForEditor < MIN_EDITOR_WIDTH) {
            const maxDocsWidth = window.innerWidth - activityRailWidth - MIN_EDITOR_WIDTH;
            if (maxDocsWidth > 400) {
                setDocsWidth(maxDocsWidth);
            }
        }
    }, [docsAsSplit, availableForEditor, setDocsWidth]);

    // Desktop layout with Activity Rail
    return (
        <div className="app-shell h-screen bg-[#0a0e14] flex overflow-hidden">
            {/* Activity Rail - always visible */}
            <div data-activity-rail>
                <ActivityRail
                    activeItem={activePanel}
                    onItemClick={handleRailClick}
                />
            </div>

            {/* Pop-Out Panel - transient */}
            <PopOutPanel
                isOpen={activePanel !== null}
                onClose={handleClosePanel}
                title={activePanel ? PANEL_TITLES[activePanel] : ''}
            >
                {renderPanelContent()}
            </PopOutPanel>

            {/* Main Content Area - shrinks when docs are pinned */}
            <main
                className="flex-1 flex flex-col h-full overflow-hidden transition-all duration-200"
                style={docsAsSplit ? { marginRight: docsWidth } : undefined}
            >
                {/* Global Top Bar - receives config from pages via context */}
                <AppTopBar
                    breadcrumbs={config.breadcrumbs}
                    centerContent={config.centerContent}
                    rightActions={config.rightActions}
                />

                {/* Page Content */}
                <div className="flex-1 overflow-hidden">
                    <Outlet />
                </div>
            </main>

            {/* Documentation Drawer - fixed position, acts as split when pinned */}
            <DocsDrawer hidden={focusMode} />

            {/* Focus Mode Indicator */}
            {focusMode && (
                <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-[#181825] border border-gray-700 rounded-lg px-4 py-2 shadow-lg flex items-center gap-3">
                    <span className="text-sm text-gray-300">Focus Mode</span>
                    <kbd className="px-1.5 py-0.5 bg-[#0f1923] rounded text-xs text-gray-400">Ctrl+Alt+F</kbd>
                    <span className="text-xs text-gray-500">to exit</span>
                </div>
            )}
        </div>
    );
};

export default AppShell;
